import React, { useEffect, useState, createContext, useContext } from 'react';
interface CartItem {
  id: string;
  name: string;
  price: number;
  type: 'training' | 'greens' | 'mushrooms';
  details?: string;
  quantity: number;
  startDate?: Date;
}
interface DiscountInfo {
  couponCode: string;
  discountAmount: number;
  selectedPlanForDiscount: string | null;
  couponApplied: boolean;
}
interface CartContextType {
  items: CartItem[];
  addItem: (item: Omit<CartItem, 'quantity'>) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  itemCount: number;
  discountInfo: DiscountInfo;
  applyDiscount: (info: DiscountInfo) => void;
  removeDiscount: () => void;
  cartModalOpen: boolean;
  setCartModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  lastAddedItem: string | null;
  updateItemStartDate: (id: string, date: Date) => void;
  trainingPlanWarningOpen: boolean;
  setTrainingPlanWarningOpen: React.Dispatch<React.SetStateAction<boolean>>;
  existingTrainingPlanName: string | null;
}
const CartContext = createContext<CartContextType | undefined>(undefined);
export const CartProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [lastAddedItem, setLastAddedItem] = useState<string | null>(null);
  const [trainingPlanWarningOpen, setTrainingPlanWarningOpen] = useState(false);
  const [existingTrainingPlanName, setExistingTrainingPlanName] = useState<string | null>(null);
  const [discountInfo, setDiscountInfo] = useState<DiscountInfo>({
    couponCode: '',
    discountAmount: 0,
    selectedPlanForDiscount: null,
    couponApplied: false
  });
  // Load cart from localStorage on initial render
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    const savedDiscount = localStorage.getItem('discount');
    if (savedCart) {
      try {
        setItems(JSON.parse(savedCart));
      } catch (e) {
        console.error('Failed to parse cart from localStorage', e);
      }
    }
    if (savedDiscount) {
      try {
        setDiscountInfo(JSON.parse(savedDiscount));
      } catch (e) {
        console.error('Failed to parse discount from localStorage', e);
      }
    }
  }, []);
  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(items));
  }, [items]);
  // Save discount info to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('discount', JSON.stringify(discountInfo));
  }, [discountInfo]);
  const addItem = (itemToAdd: Omit<CartItem, 'quantity'>) => {
    // Check if trying to add a training plan when one already exists
    if (itemToAdd.type === 'training') {
      const existingTrainingPlan = items.find(item => item.type === 'training');
      if (existingTrainingPlan && existingTrainingPlan.id !== itemToAdd.id) {
        setExistingTrainingPlanName(existingTrainingPlan.name);
        setTrainingPlanWarningOpen(true);
        return;
      }
    }
    setItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === itemToAdd.id);
      if (existingItem) {
        // For training plans, don't increase quantity
        if (itemToAdd.type === 'training') {
          return prevItems;
        }
        return prevItems.map(item => item.id === itemToAdd.id ? {
          ...item,
          quantity: item.quantity + 1
        } : item);
      }
      return [...prevItems, {
        ...itemToAdd,
        quantity: 1
      }];
    });
    setLastAddedItem(itemToAdd.name);
  };
  const removeItem = (id: string) => {
    setItems(prevItems => prevItems.filter(item => item.id !== id));
    // If we're removing the item that has a discount applied, remove the discount too
    if (id === discountInfo.selectedPlanForDiscount) {
      setDiscountInfo({
        couponCode: '',
        discountAmount: 0,
        selectedPlanForDiscount: null,
        couponApplied: false
      });
    }
  };
  const updateQuantity = (id: string, quantity: number) => {
    if (quantity < 1) return;
    setItems(prevItems => prevItems.map(item => item.id === id ? {
      ...item,
      quantity
    } : item));
  };
  const updateItemStartDate = (id: string, date: Date) => {
    setItems(prevItems => prevItems.map(item => item.id === id ? {
      ...item,
      startDate: date
    } : item));
  };
  const clearCart = () => {
    setItems([]);
    setDiscountInfo({
      couponCode: '',
      discountAmount: 0,
      selectedPlanForDiscount: null,
      couponApplied: false
    });
  };
  const applyDiscount = (info: DiscountInfo) => {
    setDiscountInfo(info);
  };
  const removeDiscount = () => {
    setDiscountInfo({
      couponCode: '',
      discountAmount: 0,
      selectedPlanForDiscount: null,
      couponApplied: false
    });
  };
  return <CartContext.Provider value={{
    items,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    itemCount: items.reduce((count, item) => count + item.quantity, 0),
    discountInfo,
    applyDiscount,
    removeDiscount,
    cartModalOpen,
    setCartModalOpen,
    lastAddedItem,
    updateItemStartDate,
    trainingPlanWarningOpen,
    setTrainingPlanWarningOpen,
    existingTrainingPlanName
  }} data-id="element-1405">
      {children}
    </CartContext.Provider>;
};
export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};